/**
 * AdminIntegraciones — Panel de health-check de servicios externos.
 *
 * Muestra una tarjeta por integración con estado verde/rojo/gris, motivo
 * exacto y botón "Volver a probar". Útil para confirmar que en producción
 * todas las API keys están vivas (FD, Redsys, Cloudinary, Resend, Wialon,
 * ORS, Google Places, WhatsApp, LLM, MongoDB).
 */
import MediosPanel from "@/components/admin/MediosPanel";
import OutboundGuardPanel from "@/components/admin/OutboundGuardPanel";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Activity, RefreshCw, CheckCircle2, XCircle, MinusCircle, Loader2, Clock,
} from "lucide-react";
import { toast } from "sonner";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `hace ${diff}s`;
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
    return `hace ${Math.floor(diff / 3600)}h`;
  } catch { return iso.slice(11, 16); }
};

export default function AdminIntegraciones() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integraciones/status");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando integraciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="admin-integraciones-page">
      <PageHeader
        title="Integraciones · Health-check"
        subtitle="Estado en vivo de todos los servicios externos que usa la app"
        icon={Activity}
        action={
          <Button onClick={load} disabled={loading}
            data-testid="btn-refresh-all"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Volver a probar todo
          </Button>
        }
      />

      {/* Seguridad de envíos en preview/test */}
      <OutboundGuardPanel />

      {/* Resumen */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total" valor={data.total} color="zinc" testId="kpi-total" />
          <KPI label="Funcionando" valor={data.healthy} color="emerald" testId="kpi-healthy" />
          <KPI label="Con problemas" valor={data.down} color="rose" testId="kpi-down" />
          <KPI
            label="Comprobado" valor={fmtRelative(data.checked_at)}
            color="zinc" testId="kpi-checked-at" icon={Clock} />
        </div>
      )}

      {/* Grid de tarjetas */}
      {loading && !data && (
        <div className="text-center py-12 text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Probando integraciones…
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="integraciones-grid">
          {data.integraciones.map((it) => <IntegracionCard key={it.key} item={it} />)}
        </div>
      )}

      {/* Medios: Google Drive (archivo histórico) + migración base64→Cloudinary */}
      <MediosPanel />

      {/* Pista pie de página */}
      <div className="text-[11px] text-zinc-500 px-1 pt-4">
        💡 Si una integración aparece en rojo, copia el "motivo" y compártelo con el equipo técnico. Las marcadas como <b className="text-zinc-300">opcionales</b> pueden estar sin configurar sin afectar al funcionamiento general.
      </div>
    </div>
  );
}

function KPI({ label, valor, color, testId, icon: Icon }) {
  const colors = {
    zinc:    "bg-zinc-900/40 border-zinc-800 text-zinc-100",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
    rose:    "bg-rose-500/10 border-rose-500/30 text-rose-200",
  };
  return (
    <div className={`rounded-xl p-3 border ${colors[color]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-2xl font-bold mt-0.5">{valor}</div>
    </div>
  );
}

function IntegracionCard({ item }) {
  const [retesting, setRetesting] = useState(false);
  const [localItem, setLocalItem] = useState(item);

  const retest = async () => {
    setRetesting(true);
    try {
      // Re-pedimos el panel completo y filtramos esta key
      const { data } = await api.get("/admin/integraciones/status");
      const fresh = (data.integraciones || []).find((x) => x.key === item.key);
      if (fresh) setLocalItem(fresh);
      toast.success(fresh?.ok ? `${item.label}: OK` : `${item.label}: ${fresh?.detail?.slice(0, 80) || "fallo"}`,
        { duration: fresh?.ok ? 3000 : 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error re-comprobando");
    } finally { setRetesting(false); }
  };

  const it = localItem;
  // 3 estados visuales: OK (verde), error (rojo), no configurado (gris)
  const estado = it.ok ? "ok" : (it.configured ? "error" : "no_config");
  const cfg = {
    ok:        { bg: "bg-emerald-500/5",  border: "border-emerald-500/30", txt: "text-emerald-300", icon: CheckCircle2, label: "OK" },
    error:     { bg: "bg-rose-500/5",     border: "border-rose-500/30",    txt: "text-rose-300",    icon: XCircle,      label: "ERROR" },
    no_config: { bg: "bg-zinc-800/30",    border: "border-zinc-700",        txt: "text-zinc-500",    icon: MinusCircle,  label: it.opcional ? "OPCIONAL" : "NO CONFIG" },
  }[estado];
  const Icon = cfg.icon;

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-3.5`}
      data-testid={`integ-card-${it.key}`}>
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0 leading-none mt-0.5">{it.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-zinc-100">{it.label}</div>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.border} ${cfg.txt} font-bold flex items-center gap-0.5`}>
              <Icon className="w-3 h-3" /> {cfg.label}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{it.descripcion}</div>
          <div className={`text-xs mt-2 ${cfg.txt} break-all`} data-testid={`integ-detail-${it.key}`}>
            {it.detail}
          </div>
        </div>
        <Button size="sm" variant="ghost"
          onClick={retest} disabled={retesting}
          data-testid={`integ-retest-${it.key}`}
          className="h-7 text-[10px] text-zinc-400 hover:text-amber-300 shrink-0">
          {retesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
}
